/*----------------------------------
- HOME: FEATURE CARDS
----------------------------------*/

/**
 * "...so you can buy the right one." (`section#workflow`): the head, the five feature cards stacking on scroll (one per
 * demo tab, in the tabs' order, each with its station module render), and the call to action under the stack, as v3
 * draws it.
 *
 * @docs docs/features/website-landing-experience
 * @adr  ADR-0110
 * @rule The call to action under the stack keeps the shipped workflow id `website_landing_product_workflow_open_database`
 *       and the shipped signup href (`links.productBaseUrl`); every card link carries `home-feature-card-<id>`.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { DATABASE_SEARCH_ACTIONS } from '@uniquedomains/components/contracts/search/searchAccessCatalog';
import { Button } from '@uniquedomains/components/kit/button/Button';
import { FeatureCards, type FeatureCardItem } from '@uniquedomains/components/marketing/feature-cards/FeatureCards';

import { HOME_FEATURE_CARDS, HOME_FEATURE_CARDS_HEAD } from '@/client/catalogs/landing/home/featureCards';
import { HOME_FEATURE_CARDS_CTA } from '@/client/catalogs/landing/home/homeCopy';
import { track } from '@/client/pages/landing/lib/analytics';
import { trackConversionTrigger } from '@/client/pages/landing/lib/trackConversionTrigger';
import { trackLandingCta } from '@/client/pages/landing/lib/trackLandingCta';

/*----------------------------------
- TYPES
----------------------------------*/

type HomeFeatureCardsProps = {
    /** The shipped workflow call to action's signup href (`LANDING_WEBSITE_LINKS.productBaseUrl`). */
    ctaHref: string;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

const TRACKING_PAGE = 'landing_home';

const CARDS: FeatureCardItem[] = HOME_FEATURE_CARDS.map((card) => ({
    id: card.id,
    tint: card.tint,
    title: card.title,
    lead: card.lead,
    body: card.body,
    shot: card.shot,
    module: card.module,
    link: {
        label: card.link.label,
        href: card.link.href,
        testId: `home-feature-card-${card.id}`,
        onClick: () => trackLandingCta({ page: TRACKING_PAGE, section: 'feature_cards', label: card.id, kind: 'feature', href: card.link.href }),
    },
}));

/*----------------------------------
- COMPONENT
----------------------------------*/

const HomeFeatureCards = ({ ctaHref }: HomeFeatureCardsProps) => {
    const onCta = () => {
        trackLandingCta({
            page: TRACKING_PAGE,
            section: 'product_workflow',
            label: HOME_FEATURE_CARDS_CTA,
            kind: 'product',
            href: ctaHref,
            feature: 'databaseAccess',
            action: DATABASE_SEARCH_ACTIONS.unlockDatabase,
        });
        track('cta_open_screener_click', { section: 'product_workflow' });
        trackConversionTrigger('website_landing_product_workflow_open_database', {
            action: DATABASE_SEARCH_ACTIONS.unlockDatabase,
            feature: 'databaseAccess',
            href: ctaHref,
            page: ctaHref,
            section: 'landing_home_workflow_bento',
        });
    };

    return (
        <section class="sec" id="workflow">
            <div class="wrap">
                <div class="sh rv">
                    <h2>{HOME_FEATURE_CARDS_HEAD.title}</h2>
                    <p>{HOME_FEATURE_CARDS_HEAD.lead}</p>
                </div>
                <FeatureCards cards={CARDS} />
                <div class="more rv">
                    <Button variant="ink" size="md" cta href={ctaHref} data-testid="home-feature-cards-cta" onClick={onCta}>
                        {HOME_FEATURE_CARDS_CTA}
                    </Button>
                </div>
            </div>
        </section>
    );
};

export default HomeFeatureCards;
